import { isThreadIdConflictError } from '@loadsmart/ai-chat'

/** Django refused the chat token: the user must sign in again. */
export class ChatSessionExpiredError extends Error {
  constructor() {
    super('The chat session expired')
    this.name = 'ChatSessionExpiredError'
  }
}

export type ChatErrorKind = 'sign-in' | 'busy' | 'unavailable-thread' | 'generic'

export type ChatErrorDescription = { kind: ChatErrorKind; message: string }

const FAILED_REQUEST = /^Request failed with (\d{3})/

function statusFromMessage(value: unknown): number | undefined {
  const message = value instanceof Error ? value.message : undefined
  const match = message ? FAILED_REQUEST.exec(message) : null

  return match ? Number(match[1]) : undefined
}

/**
 * `@loadsmart/ai-chat` has no response hook, and reports a failed request as
 * `Error("Request failed with <status>: <body>")`, directly or as the `cause`
 * of a thread error. This is the only place that reads that message.
 */
export function statusFromChatError(error: unknown): number | undefined {
  return statusFromMessage(error) ?? statusFromMessage((error as { cause?: unknown } | null)?.cause)
}

const SIGN_IN: ChatErrorDescription = {
  kind: 'sign-in',
  message: 'Your data access expired. Sign in again to keep asking questions.',
}

const BUSY: ChatErrorDescription = {
  kind: 'busy',
  message: 'An answer is still being written in this conversation. Wait for it to finish.',
}

export const UNAVAILABLE_THREAD: ChatErrorDescription = {
  kind: 'unavailable-thread',
  message: "This conversation isn't available.",
}

const GENERIC: ChatErrorDescription = {
  kind: 'generic',
  message: 'The answer failed. Try again.',
}

export function describeChatError(error: unknown): ChatErrorDescription {
  // The library wraps whatever a request builder throws while it reads a
  // thread, so an expired session can arrive as the cause of a thread error.
  const cause = (error as { cause?: unknown } | null)?.cause

  if (error instanceof ChatSessionExpiredError || cause instanceof ChatSessionExpiredError) {
    return SIGN_IN
  }

  const status = statusFromChatError(error)

  // 400 is a send without an Opendock token and 424 is a data MCP that refused
  // it; for the user all three mean the same thing.
  if (status === 401 || status === 400 || status === 424) return SIGN_IN

  // The library reads a 404 as an empty thread, so only a conflict says "not yours".
  if (isThreadIdConflictError(error)) return status === 409 ? BUSY : UNAVAILABLE_THREAD

  return GENERIC
}
